import {
  Box,
  FormControl,
  FormLabel,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import NMobileLogo from '../../../../assets/images/networks/9Mobile.svg';
import AirtelLogo from '../../../../assets/images/networks/Airtel.svg';
import GloLogo from '../../../../assets/images/networks/Glo.svg';
import MTNLogo from '../../../../assets/images/networks/MTN.svg';
import {
  CustomDrawerBackButton,
  CustomDrawerFooter,
  CustomDrawerHeader,
  CustomFormErrorMessage,
  PrimaryButton,
} from '../../../../components/custom';
import CustomDropdown from '../../../../components/custom/CustomDropdown/CustomDropdown';
import TransactionReceipt from '../../../../components/custom/TransactionReceipt';
import { phoneNumberValidation } from '../../../../constants/validationConstants';
import {
  currencyUnformat,
  numberFormatWithDecimals,
  parsePhoneNumber,
} from '../../../../helpers/utilityFunctions';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { showAlert } from '../../../../store/slices/appToast/appToastSlice';
import { getBillTransactionsAsync } from '../../../../store/slices/billsPayment/billTransactionsSlice';
import { IGetBillers } from '../../../../store/slices/billsPayment/getBillersCategorySlice';
import {
  payBillAsync,
  resetPayBill,
} from '../../../../store/slices/billsPayment/payBillSlice';
import CreateTransactionPin from '../../../TransactionPin/CreateTransactionPin';
import { TransactionPinForm } from '../TransactionPinForm';
import AirtimePurchaseSummary from './AirtimePurchaseSummary';

interface IBuyAirtimeFormProps {
  networks: IGetBillers[];
  selectedBillCategory: string;
  initialCustomerId?: string;
  initialBillerName?: string;
  handleClose: () => void;
}

export const getNetworkIcon = (networkName: string) => {
  switch (networkName) {
    case 'MTN Airtime':
      return <MTNLogo />;

    case 'Airtel Airtime':
      return <AirtelLogo />;

    case 'GLO Airtime':
      return <GloLogo />;

    case '9Mobile Airtime':
      return <NMobileLogo />;

    default:
      return null;
  }
};

export const BuyAirtimeForm = ({
  networks,
  selectedBillCategory,
  initialCustomerId,
  initialBillerName,
  handleClose,
}: IBuyAirtimeFormProps) => {
  const dispatch = useAppDispatch();

  const [billItem] = useState('');
  const [, setBillItemId] = useState('');

  const [page] = useState(1);
  const [limit] = useState(10);

  const [, setIsFixedPrice] = useState(false);

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  const [step, setStep] = useState(0);

  const [amountLimitError, setAmountLimitError] = useState('');

  const [insufficientBalanceError, setInsufficientBalanceError] = useState('');

  const { value } = useAppSelector((state) => state.auth);

  const { value: acctBalValue } = useAppSelector(
    (state) => state.accountBalance
  );
  const { value: outletsValue } = useAppSelector((state) => state.getOutlets);

  const availableBalance = acctBalValue?.data?.availableBalance;
  const availableBalanceInNaira = availableBalance ? availableBalance / 100 : 0;

  const networkNameMappings: Record<
    string,
    { name: string; icon: JSX.Element }
  > = {
    'MTN Airtime': {
      name: 'MTN NG',
      icon: <Image src={MTNLogo} alt="" />,
    },
    'GLO Airtime': {
      name: 'Glo',
      icon: <Image src={GloLogo} alt="" />,
    },
    '9Mobile Airtime': {
      name: '9Mobile',
      icon: <Image src={NMobileLogo} alt="" />,
    },
    'Airtel Airtime': {
      name: 'Airtel NG',
      icon: <Image src={AirtelLogo} alt="" />,
    },
  };
  const getMappedNetwork = (networkName: string) => {
    const mappedNetwork = networkNameMappings[networkName] || {
      name: networkName,
    };

    return {
      name: mappedNetwork.name,
      icon: mappedNetwork.icon ? (
        <Box as="span" ml={2}>
          {mappedNetwork.icon}
        </Box>
      ) : null,
    };
  };

  const getMappedNetworkForDropdown = (networkName: string) => {
    return getMappedNetwork(networkName);
  };

  useEffect(() => {
    dispatch(resetPayBill());
  }, [dispatch]);

  const networkOptions = useMemo(() => {
    return networks.map((network) => ({
      id: network.id ?? '',
      name: network.name ?? '',
      icon: getNetworkIcon(network.name ?? ''),
    }));
  }, [networks]);

  const initialValues = {
    network: initialBillerName ?? '',
    customerId: initialCustomerId ?? '',
    amount: '',
  };

  const validationSchema = Yup.object().shape({
    network: Yup.string().required('Network is required'),
    customerId: phoneNumberValidation,
    amount: Yup.string()
      .required('Amount is required')
      .test('min', 'Amount must be at least ₦100', (value) => {
        const numericValue = currencyUnformat(value);
        return numericValue >= 100;
      }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Do nothing
    },
  });

  const {
    errors,
    touched,
    values,
    getFieldProps,
    isValid,
    setFieldValue,
    handleSubmit,
  } = formik;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || (/^\+?\d*$/.test(value) && value.length <= 14)) {
      setFieldValue('customerId', value);
    }
  };

  const billItems = useMemo(
    () =>
      networks?.find((n) => n.name === values.network)
        ?.currentProviderBillerItems ?? [],
    [networks, values.network]
  );

  const billerId = networks?.find((n) => n.name === values.network)?.id;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue.endsWith('..') || /^(.*?\..*?){2}.*$/.test(inputValue)) {
      return;
    }

    const unformattedValue = currencyUnformat(inputValue);

    if (unformattedValue < 100) {
      setAmountLimitError('Amount cannot be lesser than ₦100');
    } else {
      setAmountLimitError('');
    }

    if (unformattedValue > availableBalanceInNaira) {
      setInsufficientBalanceError('Insufficient balance');
    } else {
      setInsufficientBalanceError('');
    }

    if (unformattedValue === 0) {
      setFieldValue('amount', '');
      setAmountLimitError('');
      setInsufficientBalanceError('');

      return;
    }

    let formattedValue = numberFormatWithDecimals(unformattedValue);

    if (inputValue.endsWith('.')) {
      formattedValue = `${numberFormatWithDecimals(unformattedValue)}.`;
    }

    setFieldValue('amount', formattedValue);
  };

  const customerName = `${value?.data?.user?.firstName} ${value?.data?.user?.lastName}`;
  const customerAccountNo = acctBalValue?.data?.accountNumber;
  const accountNameFromStorage = localStorage.getItem('accountName');

  const outletId =
    outletsValue?.data?.find(
      (outlet) =>
        outlet?.account?.accountNumber === customerAccountNo &&
        outlet?.account?.accountName === accountNameFromStorage
    )?.id ?? '';

  const billDetails = {
    customerName: customerName,
    customerId: parsePhoneNumber(values.customerId) ?? '',
    billCategory: 'AIRTIME',
    billItemId: billItems[0]?.id ?? '',
    billerName: values.network,
    billerId: billerId ?? '',
  };

  const handleFinalSubmit = async (pinValues: { pin: string }) => {
    const response = await dispatch(
      payBillAsync({
        outletId,
        amount: currencyUnformat(values.amount),
        customerId: parsePhoneNumber(values.customerId) || '',
        customerAccountNo: customerAccountNo ?? 0,
        customerName: customerName,
        billItemId: billItems[0]?.id ?? '',
        pin: pinValues.pin || '',
        billCategory: selectedBillCategory || 'AIRTIME',
        billProvider: values.network,
      })
    );

    if (response?.meta?.requestStatus === 'fulfilled') {
      dispatch(
        getBillTransactionsAsync({
          accountNo: customerAccountNo,
          category: selectedBillCategory.toUpperCase(),
          page,
          limit,
        })
      );
    }
  };

  const { status: paymentStatus, value: paymentValue } = useAppSelector(
    (state) => state.billsPayment
  );

  useEffect(() => {
    if (billItem) {
      setIsFixedPrice(false);
      const selectedBillItem = billItems.find((b) => b.name === billItem);
      if (selectedBillItem?.isFixedPrice) {
        setFieldValue(
          'amount',
          selectedBillItem?.amount ? `${selectedBillItem?.amount}` : '0'
        );
        setIsFixedPrice(true);
      }
    }
  }, [billItem, billItems, setFieldValue]);

  const handleGoForward = () => {
    if (billItems.length > 0 && isValid) {
      setBillItemId(billItems[0].id ?? '');
      setStep((prevStep) => prevStep + 1);
    } else if (billItems.length === 0) {
      dispatch(
        showAlert({
          status: 'info',
          description: 'Service Provider Unavailable',
        })
      );
    }
  };

  const handleGoBack = () => {
    setStep((currentStep) => currentStep - 1);
  };

  const renderStep = () => {
    const steps = [
      <Box display="flex" flexDirection="column" py="18px" key="1">
        <CustomDrawerHeader startsFromBeginning>Airtime</CustomDrawerHeader>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={8} flex="1">
              <Box mb={6}>
                <Text fontWeight="500" color="#000" mb={1}>
                  Recipient Information
                </Text>
                <Text color="gray.300" fontSize=".875rem">
                  Streamline transactions with seamless information management
                </Text>
              </Box>

              <FormControl
                id="network"
                isInvalid={Boolean(touched.network && errors.network)}
              >
                <FormLabel>Network</FormLabel>
                <CustomDropdown
                  options={networkOptions || []}
                  placeholder="Select an option"
                  getMappedNetwork={getMappedNetworkForDropdown}
                  setFieldValue={setFieldValue}
                  value={values.network}
                  fieldValue="network"
                />
                <CustomFormErrorMessage>
                  {touched.network && errors.network}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="amount"
                isInvalid={Boolean(
                  touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)
                )}
              >
                <FormLabel>Amount</FormLabel>

                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children="₦"
                    height="100%"
                  />
                  <Input
                    {...getFieldProps('amount')}
                    placeholder="Enter amount"
                    onChange={handleAmountChange}
                    isInvalid={Boolean(
                      touched.amount &&
                        (insufficientBalanceError ||
                          amountLimitError ||
                          errors.amount)
                    )}
                  />
                </InputGroup>

                <CustomFormErrorMessage>
                  {touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="customerId"
                isInvalid={Boolean(touched.customerId && errors.customerId)}
              >
                <FormLabel>Phone number</FormLabel>
                <Input
                  {...getFieldProps('customerId')}
                  as="input"
                  placeholder="Enter recipient number"
                  isInvalid={Boolean(touched.customerId && errors.customerId)}
                  onChange={handlePhoneNumberChange}
                />
                <CustomFormErrorMessage>
                  {touched.customerId && errors.customerId}
                </CustomFormErrorMessage>
              </FormControl>
            </Stack>
            <CustomDrawerFooter>
              <PrimaryButton
                onClick={handleGoForward}
                type="submit"
                w="full"
                isDisabled={Boolean(insufficientBalanceError)}
              >
                Continue
              </PrimaryButton>
            </CustomDrawerFooter>
          </Form>
        </FormikProvider>
      </Box>,
      <Box key="2">
        <CustomDrawerBackButton onClick={handleGoBack} />

        <AirtimePurchaseSummary
          summary={{
            network: values.network,
            phoneNumber: values.customerId,
            amount: currencyUnformat(values.amount),
            date: new Date(),
          }}
          networkPackage="Airtime"
          handleSubmit={handleGoForward}
        />
      </Box>,
      <Box key="3">
        <TransactionPinForm
          isSuccess={paymentStatus === 'success'}
          isLoading={paymentStatus === 'loading'}
          isError={paymentStatus === 'failed'}
          onSubmit={handleFinalSubmit}
          goToReceipt={handleGoForward}
          step={step}
          setStep={(currentStep) => setStep(currentStep)}
          statusBoxTitle={paymentValue?.message}
          statusBoxText=""
          billDetails={billDetails}
          handleClose={handleClose}
          twoFactor={{
            type: 'bill_payment',
            amountKobo: Math.round(currencyUnformat(values.amount) * 100),
          }}
        />
      </Box>,
      <Box key="4">
        <TransactionReceipt
          date={new Date().toDateString()}
          amount={currencyUnformat(values.amount)}
          transactionType="Bill Payment"
          transactionReference={'----'}
          senderName={accountNameFromStorage ?? customerName}
          narration="test"
          provider={values.network}
          device={values.customerId}
          transactionFee="₦0.00"
          senderAccountNumber={customerAccountNo}
          showAmountInWords={false}
        />
      </Box>,
    ];

    return <>{steps[step]}</>;
  };

  return (
    <div>
      {!userHasPin ? (
        <CreateTransactionPin setUserHasPin={setUserHasPin} />
      ) : (
        renderStep()
      )}
    </div>
  );
};
